import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, queryKeys } from '@/shared/api';

import type { Anchor, Section, SectionList } from './types';

export function useSections(workspaceId: string) {
  return useQuery({
    queryKey: queryKeys.sections.list(workspaceId),
    queryFn: ({ signal }) =>
      api.get<SectionList>(`/v1/workspaces/${workspaceId}/sections`, { signal }),
  });
}

/** The section trees of several workspaces (the Home page), under the same keys as `useSections`. */
export function useSectionsOf(workspaceIds: readonly string[]) {
  return useQueries({
    queries: workspaceIds.map((id) => ({
      queryKey: queryKeys.sections.list(id),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        api.get<SectionList>(`/v1/workspaces/${id}/sections`, { signal }),
    })),
    // one stable value for the whole set: the page re-derives its places only when data changed
    combine: (results) => ({
      data: results.map((result) => result.data),
      pending: results.some((result) => result.isPending),
    }),
  });
}

/** After any change of the tree or of a resource's place the catalogs are read again. */
function useRefresh(workspaceId: string) {
  const client = useQueryClient();
  return () =>
    Promise.all([
      client.invalidateQueries({ queryKey: queryKeys.sections.list(workspaceId) }),
      client.invalidateQueries({ queryKey: queryKeys.boards.list(workspaceId) }),
      client.invalidateQueries({ queryKey: queryKeys.databases.list(workspaceId) }),
      client.invalidateQueries({ queryKey: queryKeys.documents.list(workspaceId) }),
      client.invalidateQueries({ queryKey: queryKeys.groups.list(workspaceId) }),
    ]);
}

export function useCreateSection(workspaceId: string) {
  const refresh = useRefresh(workspaceId);
  return useMutation({
    mutationFn: (input: { name: string; parent_id: string | null } & Anchor) =>
      api.post<Section>(`/v1/workspaces/${workspaceId}/sections`, input),
    onSettled: () => refresh(),
  });
}

export function useRenameSection(workspaceId: string) {
  const refresh = useRefresh(workspaceId);
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name: string; expected_version?: number }) =>
      api.patch<Section>(`/v1/sections/${id}`, body),
    onSettled: () => refresh(),
  });
}

export function useMoveSection(workspaceId: string) {
  const refresh = useRefresh(workspaceId);
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; parent_id: string | null } & Anchor) =>
      api.post<Section>(`/v1/sections/${id}/move`, body),
    onSettled: () => refresh(),
  });
}

/** `move_to_parent` moves everything inside up one level first; boards / databases are never deleted. */
export function useDeleteSection(workspaceId: string) {
  const refresh = useRefresh(workspaceId);
  return useMutation({
    mutationFn: ({ id, contents }: { id: string; contents: 'reject' | 'move_to_parent' }) =>
      api.delete(`/v1/sections/${id}`, { query: { contents } }),
    onSettled: () => refresh(),
  });
}

/** File a board or a database in a section (`null` = the workspace root), optionally beside a sibling. */
export function usePlaceResource(workspaceId: string) {
  const refresh = useRefresh(workspaceId);
  return useMutation({
    mutationFn: ({
      kind,
      id,
      section_id,
      ...rest
    }: {
      kind: 'board' | 'database' | 'document';
      id: string;
      section_id: string | null;
      /** Inside this group of that section. */
      group_id?: string | null;
    } & Anchor) => api.post<unknown>(`/v1/${kind}s/${id}/place`, { section_id, ...rest }),
    onSettled: () => refresh(),
  });
}
